<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MeetingsAPI from 'dashboard/api/meetings';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import { useAdmin } from 'dashboard/composables/useAdmin';

const props = defineProps({
  meetingId: { type: Number, required: true },
});

const { t } = useI18n();

const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');
const { isAdmin } = useAdmin();

function canManageComment(comment) {
  return isAdmin.value || comment.user?.id === currentUser.value?.id;
}

const comments = ref([]);
const isLoading = ref(false);
const isSubmitting = ref(false);
const commentEditor = ref(null);
const submittingLabel = '...';

const editorHasContent = ref(false);

const mentionQuery = ref('');
const mentionHighlight = ref(0);
const showMentionMenu = ref(false);
const commentContainerRef = ref(null);
const mentionMenuAlign = ref('left');

function calcFlip(triggerEl, dropdownWidthPx) {
  if (!triggerEl) return 'left';
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  return rect.left + dropdownWidthPx > window.innerWidth - margin
    ? 'right'
    : 'left';
}

const filteredMentionAgents = computed(() => {
  const q = mentionQuery.value.toLowerCase();
  if (!q) return agents.value ?? [];
  return (agents.value ?? [])
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

watch(filteredMentionAgents, () => {
  mentionHighlight.value = 0;
});
watch(mentionQuery, () => {
  mentionHighlight.value = 0;
});

function getTextBeforeCaret(el) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return '';
  const range = sel.getRangeAt(0).cloneRange();
  range.selectNodeContents(el);
  range.setEnd(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset);
  return range.toString();
}

function detectMention() {
  const el = commentEditor.value;
  if (!el) return;
  const textBefore = getTextBeforeCaret(el);
  const match = textBefore.match(/@([\p{L}\p{N}_]*)$/u);
  if (match !== null) {
    mentionQuery.value = match[1];
    mentionMenuAlign.value = calcFlip(commentContainerRef.value, 224); // w-56
    showMentionMenu.value = true;
  } else {
    showMentionMenu.value = false;
    mentionQuery.value = '';
  }
}

function insertMention(agent) {
  const el = commentEditor.value;
  if (!el) return;
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;

  const textBefore = getTextBeforeCaret(el);
  const atIndex = textBefore.lastIndexOf('@');
  const charsToDelete = textBefore.length - atIndex;

  const deleteRange = sel.getRangeAt(0).cloneRange();
  deleteRange.setStart(
    deleteRange.endContainer,
    Math.max(0, deleteRange.endOffset - charsToDelete)
  );
  deleteRange.deleteContents();
  sel.removeAllRanges();
  sel.addRange(deleteRange);

  const span = document.createElement('span');
  span.className = 'mention-tag';
  span.dataset.userId = String(agent.id);
  span.dataset.userName = agent.name;
  span.contentEditable = 'false';
  span.textContent = `@${agent.name}`;
  span.style.cssText =
    'color:rgb(var(--n-brand-rgb,79 135 240));font-weight:500;';

  const space = document.createTextNode(' ');
  const insertRange = sel.getRangeAt(0);
  insertRange.insertNode(space);
  insertRange.insertNode(span);
  insertRange.setStartAfter(space);
  insertRange.setEndAfter(space);
  sel.removeAllRanges();
  sel.addRange(insertRange);

  showMentionMenu.value = false;
  mentionQuery.value = '';
  el.focus();
}

function serializeContent() {
  const el = commentEditor.value;
  if (!el) return '';
  return Array.from(el.childNodes)
    .reduce((text, node) => {
      if (node.nodeType === Node.TEXT_NODE) return text + node.textContent;
      if (node.nodeName === 'BR') return `${text}\n`;
      if (node.nodeName === 'SPAN' && node.classList.contains('mention-tag')) {
        const id = node.dataset.userId;
        const name = node.dataset.userName;
        return `${text}[@${name}](mention://user/${id}/${encodeURIComponent(name)})`;
      }
      return text + node.textContent;
    }, '')
    .trim();
}

function hasContent() {
  const el = commentEditor.value;
  if (!el) return false;
  return el.textContent.trim().length > 0;
}

function onEditorInput() {
  detectMention();
  editorHasContent.value = hasContent();
}

const { avatarColor, initials: agentInitials } = useAvatarColor();

function mentionInitials(name) {
  return agentInitials(name ?? '') || '?';
}

const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

// o conteúdo vem cru do backend e vai para v-html: escapar antes de montar
// as menções, senão qualquer membro injeta script na sessão de quem lê
function renderContent(text) {
  if (!text) return '';
  return text
    .replace(/[&<>"']/g, ch => HTML_ESCAPES[ch])
    .replace(
      /\[@([^\]]+)\]\(mention:\/\/user\/\d+\/[^)]+\)/g,
      '<span class="inline-flex items-center text-n-brand font-medium">@$1</span>'
    );
}

async function fetchComments() {
  isLoading.value = true;
  try {
    const { data } = await MeetingsAPI.listComments(props.meetingId);
    comments.value = data.data || [];
  } catch {
    comments.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function submit() {
  if (!hasContent()) return;
  isSubmitting.value = true;
  try {
    const { data } = await MeetingsAPI.createComment(
      props.meetingId,
      serializeContent()
    );
    comments.value.push(data);
    if (commentEditor.value) commentEditor.value.innerHTML = '';
    showMentionMenu.value = false;
  } catch {
    useAlert('Erro ao criar comentário');
  } finally {
    isSubmitting.value = false;
  }
}

function handleContentKeydown(event) {
  if (showMentionMenu.value) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      mentionHighlight.value = Math.min(
        mentionHighlight.value + 1,
        filteredMentionAgents.value.length - 1
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      mentionHighlight.value = Math.max(mentionHighlight.value - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const agent = filteredMentionAgents.value[mentionHighlight.value];
      if (agent) insertMention(agent);
    } else if (event.key === 'Escape') {
      showMentionMenu.value = false;
    }
    return;
  }

  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    submit();
  }
}

const confirmDeleteId = ref(null);

function requestDeleteComment(commentId) {
  confirmDeleteId.value = commentId;
}

function cancelDeleteComment() {
  confirmDeleteId.value = null;
}

async function deleteComment(commentId) {
  confirmDeleteId.value = null;
  try {
    await MeetingsAPI.destroyComment(props.meetingId, commentId);
    comments.value = comments.value.filter(c => c.id !== commentId);
  } catch {
    useAlert('Erro ao excluir comentário');
  }
}

async function togglePin(comment) {
  const prev = comment.pinned;
  comment.pinned = !prev;
  try {
    const { data } = await MeetingsAPI.updateComment(
      props.meetingId,
      comment.id,
      { pinned: !prev }
    );
    const idx = comments.value.findIndex(c => c.id === comment.id);
    if (idx !== -1) comments.value[idx] = data;
  } catch {
    comment.pinned = prev;
    useAlert('Erro ao fixar comentário');
  }
}

const editingCommentId = ref(null);
const editingInitialContent = ref('');
let editingEl = null;

function serializeEl(el) {
  if (!el) return '';
  return Array.from(el.childNodes)
    .reduce((text, node) => {
      if (node.nodeType === Node.TEXT_NODE) return text + node.textContent;
      if (node.nodeName === 'BR') return `${text}\n`;
      if (node.nodeName === 'SPAN' && node.classList.contains('mention-tag')) {
        const id = node.dataset.userId;
        const name = node.dataset.userName;
        return `${text}[@${name}](mention://user/${id}/${encodeURIComponent(name)})`;
      }
      return text + node.textContent;
    }, '')
    .trim();
}

function buildEditNodes(el, content) {
  el.textContent = '';
  const mentionRe = /\[@([^\]]+)\]\(mention:\/\/user\/(\d+)\/[^)]+\)/g;
  let last = 0;
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = mentionRe.exec(content)) !== null) {
    if (match.index > last) {
      el.appendChild(document.createTextNode(content.slice(last, match.index)));
    }
    const name = match[1];
    const id = match[2];
    const span = document.createElement('span');
    span.className = 'mention-tag';
    span.dataset.userId = id;
    span.dataset.userName = name;
    span.contentEditable = 'false';
    span.textContent = `@${name}`;
    span.style.cssText =
      'color:rgb(var(--n-brand-rgb,79 135 240));font-weight:500;';
    el.appendChild(span);
    el.appendChild(document.createTextNode(' '));
    last = mentionRe.lastIndex;
  }
  if (last < content.length) {
    el.appendChild(document.createTextNode(content.slice(last)));
  }
  el.focus();
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function onEditRefMount(el) {
  if (!el) return;
  editingEl = el;
  buildEditNodes(el, editingInitialContent.value);
}

function startEditComment(comment) {
  editingInitialContent.value = comment.content;
  editingCommentId.value = comment.id;
}

function cancelEditComment() {
  editingCommentId.value = null;
  editingInitialContent.value = '';
  editingEl = null;
}

async function saveEditComment(commentId, el) {
  const target = el || editingEl;
  const raw = serializeEl(target);
  if (!raw) return;
  try {
    const { data } = await MeetingsAPI.updateComment(
      props.meetingId,
      commentId,
      { content: raw }
    );
    const idx = comments.value.findIndex(c => c.id === commentId);
    if (idx !== -1) comments.value[idx] = data;
    cancelEditComment();
  } catch {
    useAlert('Erro ao salvar comentário');
  }
}

function wasEdited(comment) {
  return comment.updated_at && comment.updated_at !== comment.created_at;
}

function formatCommentDate(timestamp) {
  const d = new Date(timestamp * 1000);
  const today = new Date();
  const isToday =
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
  if (isToday) {
    return `Hoje ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function initials(name) {
  return agentInitials(name ?? '') || '?';
}

const newestComments = computed(() =>
  [...comments.value].sort((a, b) => {
    if (b.pinned !== a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
    const createdAtDiff = (b.created_at || 0) - (a.created_at || 0);
    return createdAtDiff || (b.id || 0) - (a.id || 0);
  })
);

onMounted(() => {
  fetchComments();
});
onUnmounted(() => {});
</script>

<template>
  <div class="flex flex-col gap-4">
    <h4 class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider">
      {{ t('AGENDA.COMMENTS.TITLE') }}
    </h4>

    <div class="flex items-start gap-1 relative">
      <div
        ref="commentContainerRef"
        class="flex-1 flex items-start gap-2 rounded-lg px-3 py-2 bg-n-alpha-1 relative"
      >
        <div
          ref="commentEditor"
          contenteditable="true"
          :data-placeholder="t('AGENDA.COMMENTS.ADD_PLACEHOLDER')"
          class="comment-editor flex-1 min-w-0 text-sm leading-5 text-n-slate-12 outline-none max-h-[400px] overflow-y-auto whitespace-pre-wrap break-words"
          @input="onEditorInput"
          @keydown="handleContentKeydown"
          @click="detectMention"
        />

        <div
          v-if="showMentionMenu && filteredMentionAgents.length"
          class="absolute bottom-full mb-1 z-50 bg-n-background border border-n-weak rounded-xl shadow-xl w-56 py-1 max-h-48 overflow-y-auto"
          :class="mentionMenuAlign === 'right' ? 'right-0' : 'left-0'"
          @mousedown.prevent
        >
          <button
            v-for="(agent, idx) in filteredMentionAgents"
            :key="agent.id"
            type="button"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors focus:outline-none"
            :class="
              idx === mentionHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : 'text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="insertMention(agent)"
          >
            <div
              class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white overflow-hidden"
              :style="
                agent.thumbnail
                  ? {
                      backgroundImage: `url(${agent.thumbnail})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : { backgroundColor: avatarColor(agent) }
              "
            >
              <span v-if="!agent.thumbnail">{{
                mentionInitials(agent.name)
              }}</span>
            </div>
            <span class="truncate">{{ agent.name }}</span>
          </button>
        </div>
      </div>

      <div class="flex flex-shrink-0 items-center gap-1">
        <button
          :disabled="isSubmitting || !editorHasContent"
          :class="
            editorHasContent && !isSubmitting
              ? 'bg-n-brand hover:bg-n-brand/90 cursor-pointer'
              : 'bg-n-slate-5 cursor-not-allowed'
          "
          class="px-4 py-1.5 text-sm font-medium text-white rounded-lg transition-colors flex-shrink-0"
          @click="submit"
        >
          {{ isSubmitting ? submittingLabel : t('AGENDA.COMMENTS.SUBMIT') }}
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center py-8">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-9" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <div
        v-for="comment in newestComments"
        :key="comment.id"
        class="group flex flex-col gap-1"
      >
        <div class="flex items-center gap-2 min-w-0">
          <img
            v-if="comment.user?.thumbnail"
            :src="comment.user.thumbnail"
            :alt="comment.user?.name"
            class="size-6 rounded-full object-cover flex-shrink-0"
          />
          <div
            v-else
            class="size-6 rounded-full bg-n-brand flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
          >
            {{ initials(comment.user?.name) }}
          </div>
          <span
            class="text-sm font-semibold text-n-slate-12 truncate flex-shrink"
            >{{ comment.user?.name }}</span
          >
          <span
            class="text-xs text-n-slate-9 whitespace-nowrap flex-shrink-0"
            >{{ formatCommentDate(comment.created_at) }}</span
          >
          <template v-if="wasEdited(comment)">
            <span
              class="text-n-slate-7 text-xs flex-shrink-0"
              aria-hidden="true"
              >·</span
            >
            <span
              class="text-xs text-n-slate-9 italic whitespace-nowrap flex-shrink-0 truncate"
            >
              {{ t('AGENDA.COMMENTS.EDITED') }}
              {{ formatCommentDate(comment.updated_at) }}
            </span>
          </template>
          <div class="flex items-center gap-1 ml-auto flex-shrink-0">
            <button
              :class="
                comment.pinned
                  ? 'text-n-brand'
                  : 'opacity-0 group-hover:opacity-100 text-n-slate-9 hover:text-n-brand'
              "
              class="transition-all"
              @click="togglePin(comment)"
            >
              <span class="i-lucide-pin size-3.5" />
            </button>
            <div
              v-if="canManageComment(comment)"
              class="flex items-center gap-1 w-0 group-hover:w-auto opacity-0 group-hover:opacity-100 overflow-hidden transition-all"
            >
              <button
                v-if="editingCommentId !== comment.id"
                class="text-n-slate-9 hover:text-n-brand"
                @click="startEditComment(comment)"
              >
                <span class="i-lucide-pencil size-3.5" />
              </button>
              <button
                class="text-n-slate-9 hover:text-n-ruby-9"
                @click="requestDeleteComment(comment.id)"
              >
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="confirmDeleteId === comment.id"
          class="flex items-center gap-3 mt-1 px-1"
        >
          <span class="text-xs text-n-slate-11">{{
            t('AGENDA.COMMENTS.DELETE_CONFIRM')
          }}</span>
          <button
            class="text-xs text-n-ruby-11 font-medium hover:opacity-80"
            @click="deleteComment(comment.id)"
          >
            {{ t('AGENDA.COMMENTS.DELETE') }}
          </button>
          <button
            class="text-xs text-n-slate-10 hover:text-n-slate-12"
            @click="cancelDeleteComment"
          >
            {{ t('AGENDA.MODAL.CANCEL') }}
          </button>
        </div>

        <div v-if="editingCommentId === comment.id">
          <div
            :ref="onEditRefMount"
            contenteditable="true"
            class="w-full text-sm text-n-slate-12 bg-n-alpha-1 rounded-lg px-3 py-2 focus:outline-none min-h-[64px] whitespace-pre-wrap break-words"
            @keydown.enter.exact.prevent="
              saveEditComment(comment.id, $event.currentTarget)
            "
            @keydown.esc="cancelEditComment"
          />
          <div class="flex gap-2 mt-1">
            <button
              class="text-xs text-n-brand font-medium hover:opacity-80 focus:outline-none"
              @click="saveEditComment(comment.id)"
            >
              {{ t('AGENDA.MODAL.SAVE') }}
            </button>
            <button
              class="text-xs text-n-slate-10 hover:text-n-slate-12"
              @click="cancelEditComment"
            >
              {{ t('AGENDA.MODAL.CANCEL') }}
            </button>
          </div>
        </div>

        <template v-else>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p
            class="text-sm text-n-slate-12 whitespace-pre-wrap break-words bg-n-alpha-1 rounded-lg px-3 py-2"
            v-html="renderContent(comment.content)"
          />
        </template>
      </div>

      <p v-if="comments.length === 0" class="text-xs text-n-slate-9 py-1">
        {{ t('AGENDA.COMMENTS.EMPTY') }}
      </p>
    </div>
  </div>
</template>

<style>
.comment-editor:empty::before {
  content: attr(data-placeholder);
  color: rgb(var(--n-slate-9));
  pointer-events: none;
}
</style>
